/**
 * The typed service registry (M2 plan §2.4): features share runtimes through `ctx.services` without importing each
 * other. A module declares its service by augmenting `ServiceMap`:
 *
 *   declare module '../app/services' { interface ServiceMap { m2: M2Runtime } }
 *
 * then one feature `provide`s it in `init` and the others read it with `get` (required) or `maybe` (optional).
 * Services are wired once at boot, never per frame.
 */

/** Service key → type. Empty here; modules add their keys by declaration merging. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- filled by module augmentation (declaration merging)
export interface ServiceMap {}

export type ServiceKey = keyof ServiceMap

/** A registry over the key → type map `M` (the app's is `ServiceMap`; tests may use their own). */
export interface Services<M extends object = ServiceMap> {
  /** Registers `value` under `key`. Throws when `key` is already provided (two providers are a wiring bug). */
  provide<K extends keyof M>(key: K, value: M[K]): void
  /** The service under `key`. Throws naming the key when nobody provided it (yet: check the feature order). */
  get<K extends keyof M>(key: K): M[K]
  /** The service under `key`, or undefined when nobody provided it (e.g. outside career mode). */
  maybe<K extends keyof M>(key: K): M[K] | undefined
  /** True when `key` was provided. */
  has(key: keyof M): boolean
}

export function createServices<M extends object = ServiceMap>(): Services<M> {
  const map = new Map<keyof M, unknown>()
  return {
    provide<K extends keyof M>(key: K, value: M[K]): void {
      if (map.has(key)) throw new Error(`Service "${String(key)}" is already provided`)
      map.set(key, value)
    },
    get<K extends keyof M>(key: K): M[K] {
      if (!map.has(key)) throw new Error(`Service "${String(key)}" is not provided`)
      return map.get(key) as M[K]
    },
    maybe<K extends keyof M>(key: K): M[K] | undefined {
      return map.get(key) as M[K] | undefined
    },
    has(key: keyof M): boolean {
      return map.has(key)
    },
  }
}
